<template>
    <div
        :id="id"
        class="list-view-wrapper"
        :class="{ error }"
    >
        <template v-if="modelValue?.length || initialValue?.length">
            <template
                v-for="(item, index) in items"
                :key="item.index"
            >
                <ListViewItem
                    :item="item"
                    :model-value="modelValue?.[index] || initialValue?.[index] || null"
                    :item-index="index"
                    :parent-id="id"
                    :expanded="expanded"
                    :allow-remove="allowAddRemove && items.length > 1"
                    @update:model-value="(v: string[], componentIndex: number) => handleComponentValueUpdate(v, index, componentIndex)"
                    @download-document="emit('download-document', $event)"
                    @remove-item="handleItemRemove(index)"
                />
                <ErrorText
                    v-if="error"
                    :error="error"
                />
            </template>
        </template>
        <div
            v-if="allowAddRemove"
            class="list-view-add"
        >
            <button
                type="button"
                class="minor-btn"
                @click="handleItemAdded"
            >
                {{ t('list-view.add') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { clone } from 'widget-components/utilities/clone'
import type { ListItem } from 'widget-components/types'
import ErrorText from 'widget-components/components/ErrorText'
import ListViewItem from './ListViewItem.vue'
import { useI18n } from 'widget-components/utilities/i18n'

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    items: ListItem[]
    modelValue: Array<string[]> | null
    expanded: boolean
    error?: string
    initialValue: Array<string[]> | null
    allowAddRemove?: boolean
}>()
const emit = defineEmits(['update:modelValue', 'download-document', 'items:add', 'items:remove'])

const { t } = useI18n()

onMounted(() => {
    if (props.initialValue) {
        emit('update:modelValue', props.initialValue)
    }
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const handleComponentValueUpdate = (v: any, index: number, componentIndex: number) => {
    const model = clone(props.modelValue || props.initialValue) || []
    model[index][componentIndex] = v
    emit('update:modelValue', model)
}

const handleItemAdded = () => {
    const model = clone(props.modelValue || props.initialValue) || []
    model.push([])
    emit('update:modelValue', model)
    emit('items:add')
}

const handleItemRemove = (index: number) => {
    const model = clone(props.modelValue || props.initialValue) || []
    model.splice(index, 1)
    emit('update:modelValue', model)
    emit('items:remove', index)
}

</script>

<style lang="sass" scoped>
.list-view-add
    display: flex
    justify-content: flex-end

@import "./ListView.sass"
</style>

